import React, { useState, useEffect } from 'react';
import { DocumentTextIcon, PlusIcon, TrashIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { GasContract, CreateGasContractData, GasContractType, GasContractStatus, GasPod } from '../../types/gas';
import { gasContractService, gasPodService } from '../../services/gasService';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

interface GasContractsSectionProps {
  customerId: string;
}

const GasContractsSection: React.FC<GasContractsSectionProps> = ({ customerId }) => {
  const { t } = useLanguage();
  const [contracts, setContracts] = useState<GasContract[]>([]);
  const [gasPods, setGasPods] = useState<GasPod[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingContractId, setEditingContractId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<CreateGasContractData>>({
    contractType: 'energy',
    startDate: '',
    endDate: '',
    status: 'active',
    currency: 'HUF',
    priceUnit: 'HUF/kWh',
    isFixedPrice: true,
    gasPodIds: []
  });

  const loadContracts = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [contractData, podData] = await Promise.all([
        gasContractService.getGasContractsByCustomerId(customerId),
        gasPodService.getGasPodsByCustomerId(customerId)
      ]);
      setContracts(contractData);
      setGasPods(podData);
    } catch (error) {
      console.error('Error loading gas contracts:', error);
      toast.error(t('gas.messages.loadError', { name: t('gas.sections.contracts') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContracts();
  }, [customerId]);

  const resetForm = () => {
    setFormData({
      contractType: 'energy',
      startDate: '',
      endDate: '',
      status: 'active',
      currency: 'HUF',
      priceUnit: 'HUF/kWh',
      isFixedPrice: true,
      gasPodIds: []
    });
    setEditingContractId(null);
  };

  const handleSave = async () => {
    if (!formData.contractType) {
      toast.error(t('gas.contract.errors.contractTypeRequired'));
      return;
    }
    if (!formData.startDate) {
      toast.error(t('gas.contract.errors.startDateRequired'));
      return;
    }

    const payload = {
      contractType: formData.contractType as GasContractType,
      startDate: formData.startDate,
      endDate: formData.endDate?.trim() || undefined,
      contractNumber: formData.contractNumber?.trim() || undefined,
      capacityKwh: formData.capacityKwh ? Number(formData.capacityKwh) : undefined,
      capacityM3: formData.capacityM3 ? Number(formData.capacityM3) : undefined,
      maxHourlyCapacityM3: formData.maxHourlyCapacityM3 ? Number(formData.maxHourlyCapacityM3) : undefined,
      price: formData.price ? Number(formData.price) : undefined,
      priceUnit: formData.priceUnit,
      currency: formData.currency,
      spread: formData.spread ? Number(formData.spread) : undefined,
      vatRate: formData.vatRate ? Number(formData.vatRate) : undefined,
      isFixedPrice: formData.isFixedPrice,
      gasPodIds: formData.gasPodIds,
      status: formData.status as GasContractStatus || 'active',
      notes: formData.notes?.trim() || undefined
    };

    try {
      if (editingContractId) {
        await gasContractService.updateGasContract(editingContractId, payload);
        toast.success(t('gas.messages.saveSuccess', { name: t('gas.sections.contracts') }));
      } else {
        await gasContractService.createGasContract({ customerId, ...payload });
        toast.success(t('gas.messages.addSuccess', { name: t('gas.sections.contracts') }));
      }
      setShowAdd(false);
      resetForm();
      loadContracts();
    } catch (error) {
      console.error('Error saving gas contract:', error);
      toast.error(t('gas.messages.saveError', { name: t('gas.sections.contracts') }));
    }
  };

  const handleEdit = (contract: GasContract) => {
    setFormData({
      contractType: contract.contractType,
      startDate: contract.startDate,
      endDate: contract.endDate || '',
      status: contract.status,
      contractNumber: contract.contractNumber || '',
      capacityKwh: contract.capacityKwh,
      capacityM3: contract.capacityM3,
      maxHourlyCapacityM3: contract.maxHourlyCapacityM3,
      price: contract.price,
      priceUnit: contract.priceUnit,
      currency: contract.currency,
      spread: contract.spread,
      vatRate: contract.vatRate,
      isFixedPrice: contract.isFixedPrice,
      gasPodIds: contract.gasPodIds || [],
      notes: contract.notes || ''
    });
    setEditingContractId(contract.id);
    setShowAdd(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('gas.messages.confirmDelete'))) return;
    try {
      await gasContractService.deleteGasContract(id);
      toast.success(t('gas.messages.deleteSuccess', { name: t('gas.sections.contracts') }));
      loadContracts();
    } catch (error) {
      console.error('Error deleting gas contract:', error);
      toast.error(t('gas.messages.deleteError', { name: t('gas.sections.contracts') }));
    }
  };

  const getContractTypeLabel = (type: GasContractType) => {
    switch (type) {
      case 'energy': return 'Energia (molekula)';
      case 'rhd_variable': return 'RHD forgalom-arányos';
      case 'rhd_fixed': return 'RHD fix / teljesítmény';
      default: return type;
    }
  };

  const getStatusLabel = (status: GasContractStatus) => {
    switch (status) {
      case 'draft': return 'Vázlat';
      case 'active': return 'Aktív';
      case 'expired': return 'Lejárt';
      case 'terminated': return 'Felmondott';
      default: return status;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <DocumentTextIcon className="h-6 w-6 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-900">{t('gas.sections.contracts')}</h2>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          {showAdd ? t('gas.common.cancel') : t('gas.common.new')}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">{t('gas.common.loading')}</p>
      ) : contracts.length === 0 ? (
        <p className="text-sm text-gray-500">{t('gas.common.noData')}</p>
      ) : (
        <div className="space-y-2">
          {contracts.map(contract => (
            <div key={contract.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-semibold text-gray-900">{contract.contractNumber || '—'}</span>
                  <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">{getContractTypeLabel(contract.contractType)}</span>
                  <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full">{getStatusLabel(contract.status)}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {contract.startDate} {contract.endDate ? `→ ${contract.endDate}` : ''}
                </p>
                {(contract.price || contract.capacityKwh || contract.capacityM3) && (
                  <p className="text-xs text-gray-600 mt-0.5">
                    {contract.price && <span className="font-medium text-green-700">{contract.price} {contract.priceUnit}</span>}
                    {contract.capacityKwh && <span className="ml-2">{contract.capacityKwh} kWh</span>}
                    {contract.capacityM3 && <span className="ml-2">{contract.capacityM3} m³</span>}
                  </p>
                )}
                {contract.gasPodIds && contract.gasPodIds.length > 0 && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    📍 {contract.gasPodIds.length} kapcsolódó POD
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handleEdit(contract)}
                  className="p-2 text-gray-600 hover:bg-purple-50 hover:text-purple-600 rounded-lg transition-colors"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(contract.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
          <h3 className="text-sm font-semibold text-purple-900 mb-2">
            {editingContractId ? t('gas.common.edit') : t('gas.common.new')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.fields.contractNumber')}</label>
              <input
                type="text"
                value={formData.contractNumber || ''}
                onChange={e => setFormData({ ...formData, contractNumber: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder={t('gas.contract.fields.placeholder.contractNumber')}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.fields.contractType')} *</label>
              <select
                value={formData.contractType || 'energy'}
                onChange={e => setFormData({ ...formData, contractType: e.target.value as GasContractType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="energy">{t('gas.contract.types.energy')}</option>
                <option value="rhd_variable">{t('gas.contract.types.rhd_variable')}</option>
                <option value="rhd_fixed">{t('gas.contract.types.rhd_fixed')}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.fields.startDate')} *</label>
              <input
                type="date"
                value={formData.startDate || ''}
                onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.fields.endDate')}</label>
              <input
                type="date"
                value={formData.endDate || ''}
                onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.fields.price')}</label>
              <input
                type="number"
                step="0.0001"
                value={formData.price || ''}
                onChange={e => setFormData({ ...formData, price: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.fields.priceUnit')}</label>
              <select
                value={formData.priceUnit || 'HUF/kWh'}
                onChange={e => setFormData({ ...formData, priceUnit: e.target.value as GasContract['priceUnit'] })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="HUF/kWh">HUF/kWh</option>
                <option value="EUR/kWh">EUR/kWh</option>
                <option value="HUF/MWh">HUF/MWh</option>
                <option value="EUR/MWh">EUR/MWh</option>
                <option value="HUF/m3">HUF/m³</option>
                <option value="EUR/m3">EUR/m³</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.fields.currency')}</label>
              <select
                value={formData.currency || 'HUF'}
                onChange={e => setFormData({ ...formData, currency: e.target.value as 'HUF' | 'EUR' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="HUF">HUF</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.status')}</label>
              <select
                value={formData.status || 'active'}
                onChange={e => setFormData({ ...formData, status: e.target.value as GasContractStatus })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="draft">{t('gas.contract.statuses.draft')}</option>
                <option value="active">{t('gas.contract.statuses.active')}</option>
                <option value="expired">{t('gas.contract.statuses.expired')}</option>
                <option value="terminated">{t('gas.contract.statuses.terminated')}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.fields.capacityKwh')}</label>
              <input
                type="number"
                value={formData.capacityKwh || ''}
                onChange={e => setFormData({ ...formData, capacityKwh: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.fields.capacityM3')}</label>
              <input
                type="number"
                value={formData.capacityM3 || ''}
                onChange={e => setFormData({ ...formData, capacityM3: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.fields.maxHourlyCapacityM3')}</label>
              <input
                type="number"
                value={formData.maxHourlyCapacityM3 || ''}
                onChange={e => setFormData({ ...formData, maxHourlyCapacityM3: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.fields.spread')}</label>
              <input
                type="number"
                step="0.0001"
                value={formData.spread || ''}
                onChange={e => setFormData({ ...formData, spread: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.fields.vatRate')}</label>
              <input
                type="number"
                value={formData.vatRate || ''}
                onChange={e => setFormData({ ...formData, vatRate: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="27"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isFixedPrice || false}
              onChange={e => setFormData({ ...formData, isFixedPrice: e.target.checked })}
              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
            />
            <span className="text-sm text-gray-700">{t('gas.contract.fields.isFixedPrice')}</span>
          </label>

          {/* Kapcsolódó POD-ok */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.fields.linkedPods')}</label>
            {gasPods.length === 0 ? (
              <p className="text-xs text-gray-500 italic">{t('gas.pod.noPodAvailable')}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 bg-gray-50 rounded-lg border border-gray-200">
                {gasPods.map(pod => {
                  const selected = formData.gasPodIds?.includes(pod.id) || false;
                  return (
                    <label key={pod.id} className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200 cursor-pointer hover:bg-purple-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={e => {
                          const current = formData.gasPodIds || [];
                          const updated = e.target.checked
                            ? [...current, pod.id]
                            : current.filter(id => id !== pod.id);
                          setFormData({ ...formData, gasPodIds: updated });
                        }}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate">{pod.podId}</p>
                        <p className="text-[10px] text-gray-500 truncate">{pod.address.city}, {pod.address.street}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.contract.fields.notes')}</label>
            <textarea
              value={formData.notes || ''}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setShowAdd(false);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
            >
              {t('gas.common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
            >
              {editingContractId ? t('gas.common.save') : t('gas.common.add')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GasContractsSection;
